import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, Typography, Box, Chip } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ApiService from '../../base/api/ApiService';
import CommonHeader from '../../base/views/CommonHeader';
import { Student } from '../../components/models/Student';
import { Announcement } from '../../components/models/Announcement';
import { Trip } from '../../components/models/Trip';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const StudentList: React.FC = () => {
    const navigate = useNavigate();
    const [students, setStudents] = useState<Student[]>([]);
    const [unreadAnnouncements, setUnreadAnnouncements] = useState<Announcement[]>([]);
    const [trips, setTrips] = useState<Trip[]>([]);

    useEffect(() => {
        loadStudentList();
        loadAnnouncements();
        loadTrips();
    }, []);

    const loadStudentList = async () => {
        try {
            const response = await ApiService.getStudents();
            setStudents(response || []);
        } catch (error) {
            console.error('Error loading student list:', error);
        }
    };

    const loadAnnouncements = async () => {
        try {
            const list = await ApiService.getActiveAnnouncements();
            setUnreadAnnouncements(list.filter((a) => !a.isRead).slice(0, 3));
        } catch (error) {
            console.error('Error loading announcements:', error);
        }
    };

    const loadTrips = async () => {
        try {
            const list = await ApiService.getClientTrips();
            const now = new Date();
            const upcoming = list.filter((t) => {
                if (!t.dateStart) return false;
                return new Date(t.dateStart) >= now || (t.dateEnd ? new Date(t.dateEnd) >= now : false);
            });
            setTrips(upcoming);
        } catch (error) {
            console.error('Error loading trips:', error);
        }
    };

    const tripsForStudent = (studentId: string | null) =>
        trips.filter((t) => t.studentId === studentId);

    const formatTripDates = (trip: Trip): string => {
        try {
            const start = trip.dateStart ? format(new Date(trip.dateStart), 'd MMM yyyy', { locale: ru }) : '';
            const end = trip.dateEnd ? format(new Date(trip.dateEnd), 'd MMM yyyy', { locale: ru }) : '';
            if (!end || start === end) return start;
            return `${start} — ${end}`;
        } catch {
            return '';
        }
    };

    const viewStudentDetails = (studentId: string | null) => {
        navigate(`/students/${studentId}`);
    };

    return (
        <div>
            <CommonHeader title="Список учеников" showBackButton={false} />

            {unreadAnnouncements.length > 0 && (
                <Box sx={{ m: '5px', mb: 0 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 1, pt: 1 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <CampaignIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight="bold">Объявления</Typography>
                        </Box>
                        <Typography
                            variant="caption"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate('/announcements')}
                        >
                            Все объявления
                        </Typography>
                    </Box>
                    {unreadAnnouncements.map((a) => (
                        <Card
                            key={a.id}
                            sx={{ m: '5px', cursor: 'pointer', borderLeft: '4px solid #fd5508' }}
                            onClick={() => navigate(`/announcements/${a.id}`)}
                        >
                            <CardContent sx={{ py: '8px !important' }}>
                                <Typography variant="body2" fontWeight="bold">{a.name}</Typography>
                                {(a.announce || a.message) && (
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{
                                            overflow: 'hidden',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 1,
                                            WebkitBoxOrient: 'vertical'
                                        }}
                                    >
                                        {a.announce || a.message}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {students.map((student) => {
                const studentTrips = tripsForStudent(student.id);
                return (
                    <Card key={student.id} style={{ marginBottom: '10px', margin: '5px' }}>
                        <CardContent>
                            <Typography variant="h6">{student.name}</Typography>
                            <Box mt={1} display="flex" flexDirection="row">
                                <Typography color="textSecondary">Группа:</Typography>
                                <Typography color="textSecondary">{student.groupName}</Typography>
                            </Box>

                            {studentTrips.length > 0 && (
                                <Box mt={1.5}>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                        <DirectionsBusIcon fontSize="small" color="action" />
                                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                                            Предстоящие поездки
                                        </Typography>
                                    </Box>
                                    {studentTrips.map((trip) => (
                                        <Box
                                            key={trip.tripStudentId ?? trip.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                py: 0.5,
                                                px: 1,
                                                mb: 0.5,
                                                borderRadius: 1,
                                                backgroundColor: '#f5f5f5',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => navigate(`/trips/${trip.id}?studentId=${student.id}`)}
                                        >
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {trip.name}
                                                </Typography>
                                                {trip.nameOfCompetition && (
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        {trip.nameOfCompetition}
                                                    </Typography>
                                                )}
                                                {trip.cityOfCompetition && (
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        {trip.cityOfCompetition}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="textSecondary" display="block">
                                                    {formatTripDates(trip)}
                                                </Typography>
                                                {trip.departureDateAndTime && (
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        Выезд: {(() => { try { return format(new Date(trip.departureDateAndTime!), 'd MMM yyyy, HH:mm', { locale: ru }); } catch { return trip.departureDateAndTime; } })()}
                                                    </Typography>
                                                )}
                                                {trip.gatheringPlace && (
                                                    <Typography variant="caption" color="textSecondary" display="block">
                                                        Сбор: {trip.gatheringPlace}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Chip
                                                label="Открыть"
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ ml: 1, flexShrink: 0 }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => viewStudentDetails(student?.id)}
                                style={{ marginTop: '10px' }}
                            >
                                Подробнее
                            </Button>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
};

export default StudentList;
